import {
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	type DialogProps,
	FormControlLabel,
	Grid,
	Snackbar,
	TextField,
	Typography,
	makeStyles,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import React, { type SyntheticEvent, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import { useAssetContext } from "../../../../../context/assetContext";
import { TreePickerControl } from "../../../../../controls";
import { useConfigurationAssetContext } from "../../context/ConfigurationAssetContext";
import { AssetAPI } from "../../models/AssetAPI";
import { DATA_ASSET_INITIAL_VALUES } from "../../models/InitialValueDataAsset";
import { UpdateAssetData } from "../../repositories/ConfigurationAssetRepository";
import { useAssetCodes } from "../hooks/useAssetCodes";

const useStyles = makeStyles((theme) => ({
	root: {
		"& .input-group-text": {
			display: "none",
		},
		"& .MuiPaper-root": {
			height: "auto",
		},
	},
	customDialog: {
		"& .MuiDivider-root:last-child": {
			display: "none",
		},
	},
	checkbox: {
		userSelect: "none",
	},
	modalTitle: {
		backgroundColor: "#ced2cc",
		color: "#495057",
		textAlign: "center",
	},
	modalTitleCloseWO: {
		backgroundColor: "#28a745",
		color: "white",
		textAlign: "center",
	},
	modalWarning: {
		color: "white",
		backgroundColor: "#dc3545",
		textAlign: "center",
	},
	messageTitle: {
		fontSize: 15,
		fontWeight: "bold",
		color: "#0000008A",
	},
	formControl: {
		margin: theme.spacing(2),
		minWidth: 120,
	},
	errorLabel: {
		fontSize: 12,
		color: "#F44336",
	},
}));

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
};

export const EditAssetDialog = ({ show, onHide }: Props) => {
	const classes = useStyles();
	const [fullWidth, setFullWidth] = useState(true);
	const [maxWidth, setMaxWidth] = useState<DialogProps["maxWidth"]>("sm");
	const [assetEditedSuccess, setAssetEditedSuccess] = useState<boolean>(false);
	const [parentAsset, setParentAsset] = useState("");

	const [errorEditAsset, setErrorEditAsset] = useState<string>("");
	const [isEditSubmitLoading, setIsEditeSubmitLoading] = useState(false);

	const {
		state: { assetNodeSelectedInTree, assetCodes },
		actions: { setParentAssetNameInNodeSelectedInTree },
	} = useConfigurationAssetContext();

	const { loadFilterAssets } = useAssetCodes();

	const {
		state: { allAssets },
	} = useAssetContext();

	const {
		control,
		reset,
		setValue,
		handleSubmit,
		formState: { errors },
	} = useForm<AssetAPI>({
		defaultValues: assetNodeSelectedInTree
			? assetNodeSelectedInTree
			: DATA_ASSET_INITIAL_VALUES,
	});

	const handleCancel = () => {
		onHide(false);
	};

	const handleClose = (event?: SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setAssetEditedSuccess(true);
	};

	const changeParentAssetId = (value: any, description: any) => {
		setValue("ParentAssetId", Number.parseInt(value));
		setParentAssetNameInNodeSelectedInTree(description);
	};

	const updateAssetData = async (data: AssetAPI) => {
		setIsEditeSubmitLoading(true);
		const res = await UpdateAssetData(
			assetNodeSelectedInTree.AssetId as number,
			data.AssetName,
			data.ParentAssetId as number,
			data.IsAsset,
			data.CanBeDefaultAsset,
		);
		if (res.ok) {
			setIsEditeSubmitLoading(false);
			setAssetEditedSuccess(true);
			onHide(true);
		} else {
			setIsEditeSubmitLoading(false);
			setErrorEditAsset(res.message);
		}
	};

	const handleFormSubmit = handleSubmit(updateAssetData);

	useEffect(() => {
		if (show) {
			reset();
			loadFilterAssets();
			setValue("AssetName", assetNodeSelectedInTree?.AssetName);
			setValue("ParentAssetId", assetNodeSelectedInTree?.ParentAssetId);
			const parentAssetNode = allAssets.find(
				(node: any) => node.AssetId === assetNodeSelectedInTree?.ParentAssetId,
			);
			setParentAssetNameInNodeSelectedInTree(parentAssetNode?.AssetName);
			setParentAsset(parentAssetNode?.AssetName as string);
			setValue("IsBranch", assetNodeSelectedInTree?.IsBranch);
			const assetNodeFromApi = allAssets.find(
				(node: any) => node.AssetId === assetNodeSelectedInTree?.AssetId,
			);

			setValue(
				"CanBeDefaultAsset",
				assetNodeFromApi?.CanBeDefaultAsset as boolean,
			);
		}
	}, [show]);

	return (
		<div className={classes.root}>
			<Dialog
				fullWidth={fullWidth}
				maxWidth={maxWidth}
				open={show}
				onClose={() => handleCancel()}
				disableEnforceFocus
				className={classes.root}
			>
				<form onSubmit={handleFormSubmit}>
					<Grid container style={{ padding: "15px 0 20px", height: "50%" }}>
						<Grid container item>
							<Grid item md={12} xs={12}>
								<Typography style={{ padding: "0 24px 12px", fontSize: 25 }}>
									{assetNodeSelectedInTree?.AssetName}
								</Typography>
							</Grid>
						</Grid>
						<Grid container style={{ alignItems: "center" }}>
							<DialogContent dividers style={{ padding: "10px 24px 0" }}>
								<Grid item md={12} xs={12}>
									<Controller
										name="AssetName"
										control={control}
										render={({ field: { onChange, value } }) => (
											<>
												<TextField
													label="Asset Name"
													variant="outlined"
													error={!!errors.AssetName}
													fullWidth
													margin="dense"
													autoComplete="off"
													onChange={onChange}
													value={value}
												/>
											</>
										)}
									/>
								</Grid>
								<Grid container>
									<Grid item md={12} xs={12} style={{ marginBottom: "8px" }}>
										<Grid
											item
											md={12}
											xs={12}
											style={{ padding: 0, marginTop: 10 }}
										>
											<Controller
												name="ParentAssetId"
												control={control}
												render={({}) => (
													<div title={`Parent Asset: ${parentAsset}`}>
														<TreePickerControl
															inputTitle={
																parentAsset
																	? `Parent Asset: ${parentAsset}`
																	: "Parent Asset"
															}
															title="Asset Parent"
															dataSource={assetCodes}
															value={
																assetNodeSelectedInTree?.ParentAssetId || 1
															}
															description={
																(assetNodeSelectedInTree?.ParentAssetName as string) ||
																"Plant"
															}
															onSelect={changeParentAssetId}
															selectBranch={true}
															selectActive={false}
															selectInternal={false}
														></TreePickerControl>
													</div>
												)}
											/>
										</Grid>
										<Grid
											container
											style={{ marginTop: "10px", padding: "0px" }}
										>
											<Grid item xs={6} md={6}>
												<Controller
													name="IsBranch"
													control={control}
													defaultValue={false}
													render={({ field }) => (
														<FormControlLabel
															className={classes.checkbox}
															control={
																<Checkbox
																	checked={field.value as boolean}
																	onChange={(e) =>
																		field.onChange(e.target.checked)
																	}
																	name="IsAsset"
																	color="primary"
																	disabled={true}
																/>
															}
															label="Has Children"
														/>
													)}
												/>
											</Grid>
											<Grid item xs={6} md={6}>
												<Controller
													name="CanBeDefaultAsset"
													control={control}
													defaultValue={false}
													render={({ field }) => (
														<FormControlLabel
															className={classes.checkbox}
															control={
																<Checkbox
																	checked={field.value as boolean}
																	onChange={(e) =>
																		field.onChange(e.target.checked)
																	}
																	name="CanBeDefaultAsset"
																	color="primary"
																/>
															}
															label="Can be Default Asset"
														/>
													)}
												/>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</DialogContent>
							<Grid
								item
								md={12}
								xs={12}
								justifyContent="flex-end"
								style={{ marginTop: "15px" }}
							>
								<DialogActions style={{ padding: "0px 24px" }}>
									<Grid
										container
										justifyContent="flex-end"
										style={{ margin: 0 }}
									>
										<Grid
											container
											item
											style={{
												marginLeft: "auto",
												marginTop: 5,
												marginBottom: 0,
											}}
										>
											<Grid
												item
												md={6}
												xs={12}
												style={{ paddingLeft: "0", paddingRight: "16px" }}
											>
												<Button
													fullWidth
													variant="contained"
													color="default"
													onClick={() => handleCancel()}
												>
													Cancel
												</Button>
											</Grid>
											<Grid item md={6} xs={12}>
												<Button
													fullWidth
													startIcon={
														isEditSubmitLoading && (
															<CircularProgress size="1rem" />
														)
													}
													disabled={isEditSubmitLoading}
													variant="contained"
													color="primary"
													type="submit"
												>
													Save
												</Button>
											</Grid>
										</Grid>
									</Grid>
								</DialogActions>
								<ErrorModal
									error={errorEditAsset}
									onHide={() => setErrorEditAsset("")}
								/>
								<Snackbar
									open={assetEditedSuccess}
									autoHideDuration={2500}
									onClose={handleClose}
								>
									<Alert severity="info" onClose={handleClose}>
										Document created succesfully
									</Alert>
								</Snackbar>
							</Grid>
						</Grid>
					</Grid>
				</form>
			</Dialog>
		</div>
	);
};
